import { useState, useEffect, useRef } from 'react';
import { Star, Send, CheckCircle, Camera, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import LazyImage from '../components/LazyImage';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const API = `${window.location.origin}/api`;

function StarRating({ rating, onRate, interactive = false, size = 'w-4 h-4' }) {
  return (
    <div className="flex gap-0.5" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type="button"
          onClick={() => interactive && onRate?.(i)}
          className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          disabled={!interactive}
        >
          <Star
            className={`${size} ${i <= rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-white/20'} transition-colors`}
          />
        </button>
      ))}
    </div>
  );
}

export default function FeedbackPage() {
  const [feedback, setFeedback] = useState([]);
  const [form, setForm] = useState({ customer_name: '', comment: '', rating: 5, image_url: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/feedback`).then(res => setFeedback(res.data)).catch(console.error);
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Photo too large (max 5MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/review-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, image_url: `/api/files/${res.data.storage_path}` }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = () => {
    setForm(f => ({ ...f, image_url: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.comment.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback/submit`, form);
      setSubmitted(true);
      setForm({ customer_name: '', comment: '', rating: 5, image_url: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit');
    }
    setSubmitting(false);
  };

  const avgRating = feedback.length > 0
    ? (feedback.reduce((sum, f) => sum + f.rating, 0) / feedback.length).toFixed(1)
    : '5.0';

  return (
    <div data-testid="feedback-page" className="min-h-screen pb-16">
      <SEOHead
        title="Customer Reviews"
        description="Read verified customer reviews of JS Fragrance — EDP bottled at 30% oil concentration (Extrait-level). Real ratings, photos, and stories from Filipino fragrance lovers across the Philippines."
        url="https://jsfragrances.com/reviews"
      />
      <BreadcrumbSchema crumbs={[{ name: 'Home', path: '/' }, { name: 'Reviews', path: '/reviews' }]} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Customer Reviews
          </h1>
          <div className="flex items-center justify-center gap-3 mb-2">
            <StarRating rating={Math.round(parseFloat(avgRating))} />
            <span className="text-[#D4AF37] text-lg font-sans font-medium">{avgRating}</span>
            <span className="text-white/40 text-sm font-sans">({feedback.length} reviews)</span>
          </div>
          <p className="text-white/40 text-sm font-sans">See what our customers are saying about JS Fragrance</p>
        </div>

        {/* Submit Form */}
        {!submitted ? (
          <div className="max-w-lg mx-auto mb-16 bg-[#141414] border border-white/10 p-5 sm:p-6" data-testid="feedback-form">
            <h2 className="text-xl text-white font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Share Your Experience
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Your name"
                value={form.customer_name}
                onChange={e => setForm({ ...form, customer_name: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
                data-testid="feedback-name-input"
              />
              <textarea
                placeholder="Tell us about your experience..."
                value={form.comment}
                onChange={e => setForm({ ...form, comment: e.target.value })}
                rows={3}
                className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30 resize-none"
                data-testid="feedback-comment-input"
              />

              {/* Photo upload */}
              <div data-testid="feedback-photo-upload">
                <p className="text-white/40 text-xs uppercase tracking-wider mb-2 font-sans">Photo (optional)</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="feedback-photo-input"
                />
                {form.image_url ? (
                  <div className="relative inline-block">
                    <img
                      src={form.image_url}
                      alt="Your upload"
                      className="w-24 h-24 object-cover border border-[#D4AF37]/40"
                    />
                    <button
                      type="button"
                      onClick={removePhoto}
                      data-testid="feedback-photo-remove"
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                      aria-label="Remove photo"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="feedback-photo-btn"
                    className="flex items-center gap-2 px-4 py-2 border border-dashed border-white/20 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 text-white/60 hover:text-[#D4AF37] text-xs font-sans transition-colors disabled:opacity-50"
                  >
                    {uploading
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                      : <><Camera className="w-4 h-4" /> Add a photo</>}
                  </button>
                )}
                <p className="text-white/30 text-[10px] font-sans mt-1.5">JPG / PNG / WebP, max 5MB</p>
              </div>

              <div className="flex items-center justify-between pt-2">
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1 font-sans">Your Rating</p>
                  <StarRating rating={form.rating} onRate={r => setForm({ ...form, rating: r })} interactive size="w-5 h-5" />
                </div>
                <button
                  type="submit"
                  disabled={submitting || !form.customer_name.trim() || !form.comment.trim()}
                  className="flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2.5 text-sm uppercase tracking-[0.1em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
                  data-testid="submit-feedback-btn"
                >
                  <Send className="w-3.5 h-3.5" />
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="max-w-lg mx-auto mb-16 bg-[#141414] border border-[#D4AF37]/20 p-6 text-center" data-testid="feedback-success">
            <CheckCircle className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
            <h3 className="text-white text-lg font-light mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Thank You!</h3>
            <p className="text-white/50 text-sm font-sans">Your feedback has been submitted and will appear once approved.</p>
            <button onClick={() => setSubmitted(false)} className="text-[#D4AF37] text-xs mt-3 hover:underline font-sans">Submit another</button>
          </div>
        )}

        {/* Feedback Wall */}
        {feedback.length > 0 && (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4" data-testid="feedback-wall">
            {feedback.map(fb => (
              <div key={fb.id} className="break-inside-avoid mb-4 bg-[#141414] border border-white/5 overflow-hidden group hover:border-white/10 transition-colors">
                {fb.image_url && (
                  <div className="aspect-square overflow-hidden">
                    <LazyImage src={fb.image_url} alt={fb.customer_name} className="w-full h-full group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-4">
                  <StarRating rating={fb.rating} size="w-3.5 h-3.5" />
                  <p className="text-white/70 text-sm mt-2 leading-relaxed font-sans italic">"{fb.comment}"</p>
                  <p className="text-[#D4AF37] text-xs mt-3 uppercase tracking-[0.15em] font-sans font-medium">{fb.customer_name}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {feedback.length === 0 && (
          <div className="text-center py-12">
            <p className="text-white/30 text-sm font-sans">No reviews yet. Be the first to share your experience!</p>
          </div>
        )}
      </div>
    </div>
  );
}

export { StarRating };
